import CustomModal from '@components/CustomModal';
import AddProcessForm from '@components/drawers/AddProcessForm';
import CustomDrawer from '@components/drawers/CustomDrawer';
import GridListSubheader from '@components/GridListSubheader';
import ItemWithSummaryDrag from '@components/ItemWithSummaryDrag';
import ConfirmLeaveModal from '@components/modals/ConfirmLeaveModal';
import WolfHelperNavigation from '@components/ui/WolfHelperNavigation';
import { graphQlClient } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import ProcessesGraphQL from '@graphql/process.queries';
import ServicesGraphQL from '@graphql/service.queries';
import { useLoader } from '@hooks/useLoader';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import { Area } from '@models/area.model';
import { DrawerType, SnackType } from '@models/common.model';
import { createOrUpdateOrganizationRequest } from '@models/organization.model';
import {
  CategorizedProcess,
  createOrUpdateProcessRequest,
  deleteProcessRequest,
  getProcessesByServiceIdRequest,
  getProcessesWithAreaServiceRequest,
  getProcessWithTriggeredByRequest,
  Process,
  processPolicyLinkRequest,
  reorderProcessesRequest
} from '@models/process.model';
import { getServiceByIDRequest, getServicesRequest, Service } from '@models/service.model';
import { UserRoles } from '@models/user.model';
import { Box, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { RootState } from '@redux/store';
import trackUserActivity, { ActivityType } from '@utils/ai/trackUserActivity';
import { categorizeProcesses } from '@utils/categorizeProcesses';
import { categorizeProcessesByService } from '@utils/categorizeProcessesByService';
import { DropResult, notValidDrop } from '@utils/notValidDrop';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

const ProcessList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useLocationQuery();
  const { setLoading } = useLoader();

  const locPr = useTranslation('processes');
  const localeCommon = useTranslation('common');
  const locAM = useTranslation('actionMessages');

  const [areas, setAreas] = useState<Area[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [catedProcList, setcatedProcList] = useState<CategorizedProcess>();
  const [categories, setCategories] = useState<string[]>([]);
  const [categorizedProcessesByService, setCategorizedProcessesByService] = useState<any>();
  const [categoriesByService, setCategoriesByService] = useState<any>();
  const [selectedService, setSelectedService] = useState<Partial<Service>>();
  const [selectedArea, setSelectedArea] = useState<Area>();
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState<DrawerType>(DrawerType.ADD);
  const [entityData, setEntityData] = useState<Process>();
  const [saving, setSaving] = useState(false);
  const [isGridView, setIsGridView] = useState(true);
  const [selectedNavigationItem, setSelectedNavigationItem] = useState<any>();
  const [processFormChanges, setProcessFormChanges] = useState(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [autoOpenDrawer, setAutoOpenDrawer] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recentlyDroppedId, setRecentlyDroppedId] = useState<string | null>(null);

  const { role, rememberMe } = useAppSelector((state: RootState) => state.user);
  const organizationId = useAppSelector((state: RootState) => state.organization.organizationId);
  const onboardingDetails = useAppSelector((state: RootState) => state.organization.onboardingDetails);
  const cardRefs = useRef<{ [key: string]: any | null }>({});

  // Get params & get services by areaId or all services if no ID
  useEffect(() => {
    const serviceId = query.get('serviceId');
    const openDrawer = query.get('openDrawer') === 'true';

    // Store auto-open state but don't execute yet
    setAutoOpenDrawer(openDrawer);
    setDataLoaded(false);

    if (!serviceId || serviceId === 'undefined') {
      getProcesses();
    } else {
      getProcessesByServiceId(serviceId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, organizationId]);

  // Handle tutorial mode: navigate to first process if tutorial=openFirst
  useEffect(() => {
    const tutorialMode = query.get('tutorial');
    if (tutorialMode === 'openFirst' && dataLoaded && processes.length > 0) {
      // Navigate to the first available process
      const firstProcess = processes[0];
      if (firstProcess?.processId) {
        history.push(`/processes/${firstProcess.processId}?tutorial=true`);
      }
    }
  }, [query, dataLoaded, processes, history]);

  // Auto-open drawer after data is loaded
  useEffect(() => {
    if (autoOpenDrawer && dataLoaded) {
      drawerNewOpen();
      setAutoOpenDrawer(false); // Reset to prevent multiple opens
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenDrawer, dataLoaded]);

  const refreshList = () => {
    const serviceId = selectedService?.serviceId || query.get('serviceId');
    if (!serviceId || serviceId === 'undefined') {
      getProcesses();
    } else {
      getProcessesByServiceId(serviceId);
    }
  };

  const drawerNewOpen = async () => {
    if (services.length === 0 && !selectedService) {
      dispatch(
        appendActionMessage({
          message: locPr['createServicesFirst'],
          type: SnackType.WARNING,
        }),
      );
      return;
    }

    setDrawerType(DrawerType.ADD);
    setShowDrawer(true);
  };

  const drawerEditOpen = async (id: string) => {
    setDrawerType(DrawerType.EDIT);
    setShowDrawer(true);
    const processToEdit: getProcessWithTriggeredByRequest = await graphQlClient.request(
      ProcessesGraphQL.queries.getProcessWithTriggeredBy,
      { id },
    );
    if (processToEdit) {
      setEntityData(processToEdit.getProcessWithTriggeredBy);
    }
  };

  const drawerClose = (): void => {
    setShowDrawer(false);
    setEntityData(undefined);
    refreshList();
  };

  // Drag and Drop
  const handleDragEnd = async (result: DropResult) => {
    if (notValidDrop(result)) return;
    if (saving) return; // Prevent multiple simultaneous drag operations

    let newPr: Process[] = [];
    setSaving(true);

    // Check if we have a selected service (filtering by service)
    if (!selectedService) {
      // When viewing all services, drag and drop happens within the same category
      // Find which category contains the dragged item
      const draggedProcess = processes.find((item) => item.processId === result.draggableId);
      if (!draggedProcess) return;

      const serviceId = draggedProcess?.serviceId || draggedProcess?.service?.serviceId;
      if (!serviceId) return;

      // Safety check: ensure categorizedProcessesByService is initialized
      if (!categorizedProcessesByService || Object.keys(categorizedProcessesByService).length === 0) return;

      const service = services.find((item) => item.serviceId === serviceId);
      if (!service) return;

      // Build the key for this service's categorized processes
      const serviceKey = service.area?.name + ' - ' + service.name;
      const serviceCategoryProcesses = categorizedProcessesByService[serviceKey];
      if (!serviceCategoryProcesses) return;

      // Find the category containing the dragged item
      let categoryProcesses: Process[] | null = null;
      for (const [, processes] of Object.entries(serviceCategoryProcesses as any)) {
        const processArray = processes as Process[];
        if (processArray.some((p) => p.processId === result.draggableId)) {
          categoryProcesses = [...processArray];
          break;
        }
      }

      if (!categoryProcesses) return;

      // Get the original category process IDs BEFORE reordering
      const originalCategoryProcessIds = new Set(categoryProcesses.map((p) => p.processId));

      // Reorder within the category using the drag event indices
      const [movedItem] = categoryProcesses.splice(result.source.index, 1);
      categoryProcesses.splice(result.destination!.index, 0, movedItem);

      // Rebuild the full processes array by replacing the category processes in place
      newPr = [];
      let categoryInserted = false;

      for (const process of processes) {
        if (originalCategoryProcessIds.has(process.processId)) {
          // This is a process from the reordered category
          if (!categoryInserted) {
            // Insert all reordered category processes at the first occurrence
            newPr.push(...categoryProcesses);
            categoryInserted = true;
          }
          // Skip the original process (it's already included in categoryProcesses)
        } else {
          // This is a process from a different category
          newPr.push(process);
        }
      }

      // Only send the IDs of processes in the affected service for reordering
      const processesInService = newPr.filter((item) => item.service?.serviceId === serviceId);
      const processesIds = processesInService.map((item: Process) => item.processId);

      // Optimistic update: Update UI immediately
      const oldProcesses = [...processes];
      refreshProcessList(null, null, newPr);
      setRecentlyDroppedId(result.draggableId);
      setTimeout(() => {
        setRecentlyDroppedId(null);
      }, 5000);

      try {
        const data: reorderProcessesRequest = await graphQlClient.request(ProcessesGraphQL.mutations.reorderProcesses, {
          processes: { processesIds },
        });
        if (data?.reorderProcesses.messages[0] === 'Update successful') {
          dispatch(
            appendActionMessage({
              message: locAM['processesReorderedSuccessfully'],
              type: SnackType.SUCCESS,
            }),
          );
        } else {
          // Revert on unexpected response
          refreshProcessList(null, null, oldProcesses);
        }
      } catch (e: any) {
        // Revert on error
        refreshProcessList(null, null, oldProcesses);
        showErrorSnack(e);
      } finally {
        setSaving(false);
      }
    } else {
      // When viewing a single service, processes are grouped by category
      // The drag indices are relative to the category, not the full process list
      const draggedProcess = processes.find((item) => item.processId === result.draggableId);
      if (!draggedProcess) return;

      // Find the destination process by looking at the categorized list
      let destinationProcessId: string | null = null;

      for (const [, categoryProcesses] of Object.entries(catedProcList || {})) {
        const processArray = categoryProcesses as Process[];
        if (processArray.some((p) => p.processId === result.draggableId)) {
          // This is the category containing the dragged item
          if (result.destination!.index < processArray.length) {
            destinationProcessId = processArray[result.destination!.index].processId;
          }
          break;
        }
      }

      if (!destinationProcessId) return;

      // Find actual indices in the full processes array
      newPr = [...processes];
      const sourceIndex = newPr.findIndex((item) => item.processId === result.draggableId);
      const destinationIndex = newPr.findIndex((item) => item.processId === destinationProcessId);

      if (sourceIndex === -1 || destinationIndex === -1) return;

      // Perform the reorder
      const [movedItem] = newPr.splice(sourceIndex, 1);
      newPr.splice(destinationIndex, 0, movedItem);

      const processesIds = newPr.map((item: Process) => item.processId);

      // Optimistic update: Update UI immediately
      const oldProcesses = [...processes];
      newPr.forEach((proc, index) => {
        proc.order = index + 1;
      });
      refreshProcessList(null, null, newPr);
      setRecentlyDroppedId(result.draggableId);
      setTimeout(() => {
        setRecentlyDroppedId(null);
      }, 5000);

      try {
        const data: reorderProcessesRequest = await graphQlClient.request(ProcessesGraphQL.mutations.reorderProcesses, {
          processes: { processesIds },
        });
        if (data?.reorderProcesses.messages[0] === 'Update successful') {
          dispatch(
            appendActionMessage({
              message: locAM['processesReorderedSuccessfully'],
              type: SnackType.SUCCESS,
            }),
          );
        } else {
          // Revert on unexpected response
          refreshProcessList(null, null, oldProcesses);
        }
      } catch (e: any) {
        // Revert on error
        refreshProcessList(null, null, oldProcesses);
        showErrorSnack(e);
      } finally {
        setSaving(false);
      }
    }
  };

  // CRUD Operations (incl Drawer save new/edit)
  const getProcesses = async () => {
    try {
      setLoading(true);
      setHasError(false);
      // Clear selected service and area when viewing all processes
      setSelectedService(undefined);
      setSelectedArea(undefined);
      const data: getProcessesWithAreaServiceRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessesWithAreaService,
      );
      refreshProcessList(
        null,
        null,
        data?.getProcessesWithAreaService.sort((a, b) => (a.order > b.order ? 1 : -1)),
      );
      let newServices: Service[] = [];
      data?.getProcessesWithAreaService.forEach((process: Process) => {
        if (process.service && !newServices.some((service) => process?.service?.serviceId === service.serviceId)) {
          newServices.push(process.service);
        }
      });
      // If no services found from processes, fetch services directly
      if (newServices.length === 0) {
        const servicesData: getServicesRequest = await graphQlClient.request(
          ServicesGraphQL.queries.getServices,
        );
        newServices = servicesData?.getServices || [];
      }
      const newAreas: Area[] = [];
      newServices.forEach((service: Service) => {
        if (service.area && !newAreas.some((area) => service?.area?.areaId === area.areaId)) {
          newAreas.push(service.area);
        }
      });
      const { categorizedProcessesByService, categoriesByService } = categorizeProcessesByService(
        data?.getProcessesWithAreaService,
        newServices,
      );
      setAreas(newAreas);
      setServices(newServices);
      setCategorizedProcessesByService(categorizedProcessesByService);
      setCategoriesByService(categoriesByService);
      setDataLoaded(true);
    } catch (e: any) {
      setHasError(true);
      console.error('Error getting processes');
      showErrorSnack(e);
    } finally {
      setLoading(false);
    }
  };

  const getProcessesByServiceId = async (id: string) => {
    try {
      setLoading(true);
      setHasError(false);

      const variables = { id };
      const data: getProcessesByServiceIdRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessesByServiceId,
        variables,
      );
      refreshProcessList(
        null,
        null,
        data?.getProcessesByServiceId.sort((a, b) => (a.order > b.order ? 1 : -1)),
      );
      if (data?.getProcessesByServiceId?.length === 0) {
        getServiceById(id);
      } else {
        setSelectedService(data?.getProcessesByServiceId[0]?.service);
        setSelectedArea(data?.getProcessesByServiceId?.[0]?.service?.area);
      }
      const selectedCategory = query.get('category');
      if (selectedCategory) {
        setTimeout(() => {
          scrollToCard(data?.getProcessesByServiceId[0]?.service?.name + '-' + selectedCategory);
        }, 300);
      }
      setDataLoaded(true);
    } catch (e: any) {
      setHasError(true);
      showErrorSnack(e);
    } finally {
      setLoading(false);
    }
  };

  const getServiceById = async (id: string) => {
    setLoading(true);
    try {
      setHasError(false);
      const variables = { id };
      const data: getServiceByIDRequest = await graphQlClient.request(
        ServicesGraphQL.queries.getServiceByID,
        variables,
      );
      setSelectedService(data?.getServiceByID);
      setSelectedArea(data?.getServiceByID?.area);
      setDataLoaded(true);
    } catch (e: any) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const deleteResult: deleteProcessRequest = await graphQlClient.request(ProcessesGraphQL.mutations.deleteProcess, {
        id,
      });
      if (deleteResult?.deleteProcess?.affected > 0) {
        trackUserActivity(ActivityType.PROCESS_DELETION, rememberMe, id, selectedService?.serviceId);
        const newProcesses = processes.filter((process: Process) => process.processId !== id);
        dispatch(appendActionMessage({ message: locAM['deleteSuccessful'], type: SnackType.SUCCESS }));
        refreshProcessList(null, null, newProcesses);
      }
      setShowDrawer(false);
      setEntityData(undefined);
    } catch (e: any) {
      showErrorSnack(e);
    }
  };

  const handleDrawerSave = async (process: Process, policies: { text: string; value: string }[]) => {
    if (!process.name || !process.goal || !process.serviceId) {
      dispatch(
        appendActionMessage({
          message: locAM['processNameGoalServiceRequired'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    setSaving(true);
    const isEdit = drawerType === DrawerType.EDIT;
    if (isEdit && !process.processId) {
      return appendActionMessage({
        message: localeCommon['somethingWentWrong'],
        type: SnackType.ERROR,
      });
    }
    const auxPolicy = policies?.map((policy: { text: string; value: string }) => policy.value);
    try {
      if (isEdit) {
        //Remove policies, triggers, steps from the process object since they are updated separately
        delete process.policies;
        delete process.triggers;
        delete process.steps;
        delete process.service;
        delete process.location;
        delete process.triggeredBy;
        delete process.generatedFromName;
        delete process.generatedFrom;
        delete process.lastUpdatedAt;
        delete process.currentVersion;
      }
      const query =
        isEdit && process.processId
          ? ProcessesGraphQL.mutations.updateProcess
          : ProcessesGraphQL.mutations.createProcess;
      const params = {
        process: {
          ...process,
          serviceId: !isEdit ? selectedService?.serviceId || process.serviceId : process.serviceId,
        },
      };
      const data: createOrUpdateProcessRequest = await graphQlClient.request(query, params);
      if (data?.createProcess?.processId || data?.updateProcess?.processId) {
        if (!onboardingDetails?.processCreated && !isEdit) {
          updateOrganizationProcessCreated();
        }
        // Update the linked policies
        if (auxPolicy && auxPolicy.length > 0) {
          handleLinkProcessPolicies(data?.createProcess?.processId || process?.processId, auxPolicy);
        }

        // Update the processes list
        let newProcesses: Process[] = [];
        if (isEdit) {
          const updatedProcess: Process = data?.updateProcess as Process;
          newProcesses = processes.filter(
            (auxProcess: Process) => auxProcess.processId !== process.processId,
            updatedProcess,
          );
        } else {
          newProcesses = [...processes, data.createProcess as Process];
        }
        dispatch(
          appendActionMessage({
            message: isEdit ? locAM['updateSuccessful'] : locAM['processCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
        if (!isEdit && data?.createProcess?.processId) {
          history.push(`/processes/${data.createProcess.processId}?editFirstStep=true`);
        } else {
          refreshProcessList(null, null, newProcesses);
          drawerClose();
        }
      }
    } catch (e: any) {
      showErrorSnack(e);
    }
  };

  const refreshProcessList = (
    updatedProcessIndex: number | null,
    updatedProcess: Process | null,
    newProcessesList?: Process[],
  ) => {
    setSaving(false);
    let newProcesses: Process[] = [];
    if (newProcessesList) {
      newProcesses = newProcessesList;
    } else if (updatedProcessIndex !== null && updatedProcess) {
      newProcesses = [...processes];
      newProcesses[updatedProcessIndex] = updatedProcess;
    } else {
      return;
    }
    setProcesses(newProcesses);
    const { categorizedProcesses, categories } = categorizeProcesses(newProcesses);
    setcatedProcList(categorizedProcesses);
    setCategories(categories);

    // Also update categorizedProcessesByService for the "all processes" view
    if (!selectedService && services.length > 0) {
      const { categorizedProcessesByService: newCategorizedByService, categoriesByService: newCategoriesByService } =
        categorizeProcessesByService(newProcesses, services);
      setCategorizedProcessesByService(newCategorizedByService);
      setCategoriesByService(newCategoriesByService);
    }
  };

  const handleLinkProcessPolicies = async (processId: string, policies: string[]) => {
    try {
      const updateResult: processPolicyLinkRequest = await graphQlClient.request(
        ProcessesGraphQL.mutations.linkProcessPolicy,
        {
          processId,
          policyId: JSON.stringify(policies),
        },
      );
      if (updateResult?.linkProcessPolicy?.processId) {
        const updatedProcess = processes.find((process: Process) => process.processId === processId);
        if (updatedProcess) {
          updatedProcess.policies = updateResult.linkProcessPolicy.policies;
          const updatedProcessIndex = processes.findIndex((process: Process) => process.processId === processId);
          refreshProcessList(updatedProcessIndex, updatedProcess, undefined);
        }
      }
    } catch (e: any) {}
  };

  const updateOrganizationProcessCreated = async () => {
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.updateOrganization,
        {
          organization: {
            organizationId: organizationId || '',
            onboardingDetails: {
              ...onboardingDetails,
              processCreated: true,
            },
          },
        },
      );
      if (data?.updateOrganization?.organizationId) {
        dispatch(setOrganizationData(data.updateOrganization));
      }
    } catch (error) {
      console.error('Failed to update process created status:', error);
    }
  };

  const showErrorSnack = (e: any) => {
    setSaving(false);
    dispatch(
      appendActionMessage({
        message: e?.response?.errors[0]?.message || localeCommon['requestError'],
        type: SnackType.ERROR,
      }),
    );
  };

  const handleProcessClick = (id: string, e?: React.MouseEvent) => {
    const url = `/processes/${id}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const processCardList = categories.sort().map((category: string, categoryIndex: number) => {
    return (
      <Box
        key={category}
        sx={{ scrollMargin: 100 }}
        ref={(el) => (cardRefs.current[selectedService?.name + '-' + category] = el)}>
        <div>
          <Box className="flex items-center my-8">
            <Typography variant="h4semibold" className="ml-2 cursor pointer">
              {category}
            </Typography>
          </Box>
          <ItemWithSummaryDrag
            elements={catedProcList?.[category] || []}
            elementIdName="processId"
            showDrag={true}
            isGridView={isGridView}
            hasCalendar={false}
            hasStorage={false}
            recentlyDroppedId={recentlyDroppedId}
            handleDragEnd={handleDragEnd}
            drawerEditOpen={drawerEditOpen}
            handleCardClick={handleProcessClick}
          />
        </div>
      </Box>
    );
  });

  const allAreasProcessCardList = areas.sort().map((area: Area) => {
    return (
      <div key={area.areaId}>
        {services
          .filter((service) => service.area?.areaId === area.areaId)
          .sort()
          .map((service: Service) => {
            return (
              <div key={service.serviceId}>
                {categoriesByService[service.area?.name + ' - ' + service.name].map((category: string) => {
                  return (
                    <Box
                      key={category}
                      sx={{ scrollMargin: 100 }}
                      ref={(el) => (cardRefs.current[service.name + '-' + category] = el)}>
                      <Box className="flex items-center my-8">
                        <Typography variant="h4" className="mx-2">
                          {area.name + ' / ' + service.name + ' / '}
                        </Typography>
                        <Typography variant="h4semibold" className="ml-2 cursor pointer">
                          {category}
                        </Typography>
                      </Box>

                      <ItemWithSummaryDrag
                        cardsRefs={cardRefs}
                        elements={categorizedProcessesByService[service.area?.name + ' - ' + service.name][category]}
                        isGridView={isGridView}
                        elementIdName="processId"
                        showDrag={true}
                        hasCalendar={false}
                        hasStorage={false}
                        recentlyDroppedId={recentlyDroppedId}
                        handleDragEnd={handleDragEnd}
                        drawerEditOpen={drawerEditOpen}
                        handleCardClick={handleProcessClick}
                      />
                    </Box>
                  );
                })}
              </div>
            );
          })}
      </div>
    );
  });

  const allAreasNavigation = (
    <div className='mb-28'>
      {areas.sort().map((area: Area) => {
        return (
          <div key={area.areaId}>
            {services
              .filter((service) => service.area?.areaId === area.areaId)
              .sort()
              .map((service: Service) => {
                return (
                  <div key={service.serviceId}>
                    {categoriesByService[service.area?.name + ' - ' + service.name].map((category: string) => {
                      return (
                        <div key={category}>
                          <WolfHelperNavigation
                            type={'title'}
                            label={service?.area?.name + ' - ' + service.name + ' - ' + category}
                            selected={selectedNavigationItem === category}
                            onSelect={() => {
                              scrollToCard(service.area?.name + ' - ' + service.name + '-' + category);
                            }}
                          />
                          {categorizedProcessesByService[service.area?.name + ' - ' + service.name][category].map(
                            (process: Process) => {
                              return (
                                <WolfHelperNavigation
                                  key={process.processId}
                                  type={'section'}
                                  label={process.name}
                                  selected={selectedNavigationItem === process.processId}
                                  onSelect={() => {
                                    scrollToCard(process.processId);
                                  }}
                                />
                              );
                            },
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );

  const selectedNavigation = (
    <div className='mb-28'>
      {categories.map((category: string) => {
        return (
          <div key={category}>
            <WolfHelperNavigation
              type={'title'}
              label={category}
              selected={selectedNavigationItem === category}
              onSelect={() => {
                scrollToCard(selectedService?.name + '-' + category);
              }}
            />
            {catedProcList?.[category].map((process: Process) => {
              return (
                <WolfHelperNavigation
                  key={process.processId}
                  type={'section'}
                  label={process.name}
                  selected={selectedNavigationItem === process.processId}
                  onSelect={() => {
                    scrollToCard(process.processId);
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );

  const scrollToCard = (id: string) => {
    setSelectedNavigationItem(id);
    if (cardRefs.current[id]) {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleToggleView = () => {
    setIsGridView(!isGridView);
  };

  const handleDrawerClose = () => {
    if (processFormChanges) {
      setShowConfirmLeaveModal(true);
    } else {
      drawerClose();
    }
  };

  const closeConfirmLeaveModal = () => {
    setShowConfirmLeaveModal(false);
  };

  const closeAndConfirmLeaveModal = () => {
    setShowConfirmLeaveModal(false);
    setProcessFormChanges(false);
    drawerClose();
  };

  const handleProcessFormChanges = (changes: boolean) => {
    setProcessFormChanges(changes);
  };

  return (
    <>
      <Box className='w-full mb-28'>
        <Box className="sticky top-0 z-30 bg-backgroundUI">
          <GridListSubheader
            title={localeCommon['areas']}
            titleLink="/areas"
            subTitle={selectedArea?.name || localeCommon['allAreas']}
            subTitleLink={'/services/?areaId=' + selectedService?.areaId}
            subSubTitle={selectedService?.name || locPr['allServices']}
            subSubTitleLink={'/services/' + selectedService?.serviceId}
            buttonText={role !== UserRoles.USER ? locPr['addNew'] : null}
            isGridView={isGridView}
            onToggleView={handleToggleView}
            onDrawerOpen={drawerNewOpen}
            icon="process"
            iconColor={selectedService?.color}
          />
        </Box>
        {!dataLoaded ? (
          <Box className="flex justify-center items-center h-32">
            <CircularProgress size={24} />
          </Box>
        ) : processes.length === 0 ? (
          <Box className="flex justify-center items-center h-full w-full">
            <Typography variant="h4semibold">
              {hasError ? localeCommon['requestError'] : locPr['emptyProcessMessage']}
            </Typography>
          </Box>
        ) : (
          <Box className="flex" data-tutorial-id="first-process-item">
            <Box className="w-3/4">{selectedService ? processCardList : allAreasProcessCardList}</Box>
            <Box className="w-1/5 fixed -right-1 overflow-y-scroll scrollbar-hide h-5/6">
              {selectedService ? selectedNavigation : allAreasNavigation}
            </Box>
          </Box>
        )}

        <CustomDrawer
          title={drawerType === 'edit' ? locPr['edit'] : locPr['addNew']}
          isOpen={showDrawer}
          onClose={handleDrawerClose}
          size="large">
          <AddProcessForm
            parentService={{ name: selectedService?.name || '', serviceId: selectedService?.serviceId || '' }}
            onClose={handleDrawerClose}
            handleSave={handleDrawerSave}
            handleDelete={handleDelete}
            drawerType={drawerType}
            initialData={entityData}
            saveBtnText={drawerType === DrawerType.EDIT ? locPr['editProcess'] : locPr['createProcess']}
            saving={saving}
            onChanges={handleProcessFormChanges}
          />
        </CustomDrawer>

        {showConfirmLeaveModal && (
          <CustomModal
            isOpen={!!showConfirmLeaveModal}
            title={localeCommon['confirmLeave']}
            customConfirmColor="error"
            maxWidth="600px"
            showButtons={true}
            customCancelText={localeCommon['backToEditing']}
            customConfirmText={localeCommon['discardChanges']}
            onClose={closeConfirmLeaveModal}
            onConfirm={closeAndConfirmLeaveModal}>
            <ConfirmLeaveModal />
          </CustomModal>
        )}
      </Box>
    </>
  );
};

export default ProcessList;
